import { isDefined } from "@clipboard-health/util-ts";
import CloseIcon from "@mui/icons-material/Close";
import {
  GlobalStyles,
  IconButton,
  InputAdornment,
  type SxProps,
  type TextFieldProps,
  type Theme,
} from "@mui/material";
import {
  getLocationFromPlace,
  getMapApiWithPlaces,
  parseAddressComponents,
} from "@src/appV2/lib/GoogleMaps/googleMaps";
import { TextInput } from "@src/appV2/redesign/components/TextInput";
import { type Address } from "@src/interface";
import { isEmpty } from "lodash";
import { useEffect, useRef, useState } from "react";
import { type Control, type FieldPath, type FieldValues, useController } from "react-hook-form";

export interface AddressWithCoordinates {
  address: Address;
  coordinates: {
    latitude: number;
    longitude: number;
  };
}

interface LocationAutoCompleteFieldProps<TFormValues extends FieldValues> {
  name: FieldPath<TFormValues>;
  control: Control<TFormValues>;
  placeholder?: string;
  isClearable?: boolean;
  fullWidth?: boolean;
  inputProps?: TextFieldProps["inputProps"];
  sx?: SxProps<Theme>;
}
export function LocationAutoCompleteField<TFormValues extends FieldValues>(
  props: LocationAutoCompleteFieldProps<TFormValues>
) {
  const {
    name,
    control,
    placeholder,
    isClearable = false,
    fullWidth = false,
    inputProps,
    sx,
  } = props;
  const { field, fieldState } = useController({
    name,
    control,
  });
  const { onChange: onFormFieldChange } = field;
  const [formattedAddress, setFormattedAddress] = useState<string>("");

  const locationSearchInput = useRef<HTMLInputElement>(null);
  const autoCompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  useEffect(() => {
    let shouldInitialize = true;

    async function onPlaceChanged() {
      const place: google.maps.places.PlaceResult = autoCompleteRef.current?.getPlace() ?? {};

      if (!isDefined(place.geometry)) {
        return;
      }

      const coordinates: { lat: number; lng: number } = getLocationFromPlace(place);
      const address = parseAddressComponents(place.address_components ?? []);

      onFormFieldChange({
        coordinates: {
          latitude: coordinates.lat,
          longitude: coordinates.lng,
        },
        address: {
          ...address,
          formatted: place.formatted_address,
        },
      } satisfies AddressWithCoordinates);
      if (place.formatted_address) {
        setFormattedAddress(place.formatted_address);
      }
    }

    async function initAutoSearch() {
      if (!isDefined(locationSearchInput.current)) {
        return;
      }

      const mapApi = await getMapApiWithPlaces();
      if (!shouldInitialize || !isDefined(locationSearchInput.current) || !isDefined(mapApi)) {
        return;
      }

      autoCompleteRef.current = new mapApi.places.Autocomplete(locationSearchInput.current);
      autoCompleteRef.current?.addListener("place_changed", onPlaceChanged);
    }

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    initAutoSearch();

    return () => {
      shouldInitialize = false;
      if (isDefined(autoCompleteRef.current)) {
        google.maps.event.clearInstanceListeners(autoCompleteRef.current);
        autoCompleteRef.current = null;
      }

      const pacContainers = document.querySelectorAll(".pac-container");
      pacContainers.forEach((container) => {
        container.remove();
      });
    };
  }, [onFormFieldChange]);

  useEffect(() => {
    // sets default value for field if it exists
    const defaultFormattedAddress = field.value?.address?.formatted;
    if (
      isDefined(defaultFormattedAddress) &&
      !isEmpty(defaultFormattedAddress) &&
      isEmpty(formattedAddress)
    ) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      setFormattedAddress(defaultFormattedAddress);
    }
  }, [field.value, formattedAddress]);

  return (
    <>
      <GlobalStyles
        styles={{
          ".pac-container": {
            zIndex: 2001,
          },
        }}
      />
      <TextInput
        {...field}
        size="small"
        fullWidth={fullWidth}
        inputRef={locationSearchInput}
        value={formattedAddress}
        placeholder={placeholder}
        inputProps={inputProps}
        sx={sx}
        // FIXME: Automatically disabled during ESLint config update
        error={Boolean(fieldState.error)}
        helperText={fieldState.error?.message}
        InputProps={{
          endAdornment: isClearable && !isEmpty(formattedAddress) && (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={() => {
                  onFormFieldChange(null);
                  setFormattedAddress("");
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
        onChange={(event) => {
          onFormFieldChange({
            value: event.target.value,
          });
          setFormattedAddress(event.target.value);
        }}
      />
    </>
  );
}
